import {
  Component,
  Inject,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatPaginator } from '@angular/material/paginator';

import { BarcodeTypeDTO, ProductDTO } from 'src/app/models/Products/productDTO';
import { FieldType, FilterName } from 'src/app/models/custom-table/custom-table.model';
import { ProductService } from 'src/app/shared/services/products/products/product.service';
import { SitesService } from 'src/app/shared/services/sites/sites.service';
import { SnackbarService } from 'src/app/shared/services/snackbar/snackbar.service';
import { SnackbarType } from 'src/app/models/snackbar/snackbar.model';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

@Component({
  selector: 'app-bar-code-generator-dialog',
  templateUrl: './bar-code-generator-dialog.component.html',
  styleUrls: ['./bar-code-generator-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class BarCodeGeneratorDialogComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;

  storeProductsList: ProductDTO[] = [];
  selectedProducts: ProductDTO[] = [];
  totalProducts = 0;

  barcodeTypes: BarcodeTypeDTO[] = [];
  selectedBarcodeType: any = null;
  selectedBarcodeTypeCode: string;
  barcodeValue: string;

  public isLoading$ = new BehaviorSubject<boolean>(false);

  searchTerms: string[] = [];
  isAllSelected = false;

  siteCode: any;

  isNotFuelFilter = {
    column: 'isFuel',
    fieldType: FieldType.BOOLEAN,
    operator: FilterName.EQUAL,
    value: false
  };

  constructor(
    private productService: ProductService,
    private sitesService: SitesService,
    private translate: TranslateService,
    private snackbarService: SnackbarService,
    private dialogRef: MatDialogRef<BarCodeGeneratorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { siteCode: string }
  ) { }

  ngOnInit(): void {
    this.loadProducts(0, 10);
    this.loadBarcodeTypes();
    this.siteCode = this.data.siteCode;
  }

  loadProducts(skip: number, top: number): void {
    this.isLoading$.next(true);

    const paging = { skip, top, realSize: true };
    const advancedFilter = [this.isNotFuelFilter];

    if (this.searchTerms?.length) {
      const searchFilters = this.searchTerms.map(term => ({
        column: 'description',
        operator: FilterName.CONTAINS,
        value: term,
        fieldType: FieldType.STRING
      }));
      (advancedFilter as any[]).push(...searchFilters);
    }

    this.productService.getProducts(paging, null, null, { advancedFilter }).subscribe({
      next: (res) => {
        const products = res.result.items || [];
        this.totalProducts = res.result.size;

        this.sitesService.getSiteStoreProductList(this.data.siteCode).subscribe({
          next: (res) => {
            const siteProducts = res?.result || [];
            const siteProductsMap = new Map<string, any>();
            siteProducts.forEach(sp => siteProductsMap.set(sp.productCode, sp));

            this.storeProductsList = products.map(product => {
              const siteData = siteProductsMap.get(product.code);
              return {
                ...product,
                availableInStore: !!siteData,
                productPrice: siteData?.unitPriceGross ?? product.unitPriceGross,
              };
            });

            this.updateSelectAllCheckboxState();
            this.isLoading$.next(false);
          },
          error: () => {
            this.storeProductsList = products.map(product => ({
              ...product,
              availableInStore: false,
              siteUnitPriceNet: null,
            }));
            this.isLoading$.next(false);
          }
        });
      },
      error: () => {
        this.isLoading$.next(false);
      }
    });
  }

  loadBarcodeTypes(): void {
    const paginationRequest = {
      offset: 0,
      limit: 999,
      realSize: false,
    };

    this.productService.getBarcodeTypes(paginationRequest).subscribe({
      next: (res) => {
        this.barcodeTypes = res?.result ?? res; 
      },
      error: () => {
        this.isLoading$.next(false);
        this.snackbarService.show({
          message: this.translate.instant("SiteManagement.barCodeDialog.loadBarCodesTypesError"),
          type: SnackbarType.ERROR,
        });
      }
    });
  }

  onSelectionChange(event: any) {
    const [option] = event.options;
    const { value: product } = option;

    const index = this.selectedProducts.findIndex(p => p.code === product.code);
    if (index >= 0) {
      this.selectedProducts.splice(index, 1);
    } else {
      this.selectedProducts.push(product);
    }
  }

  checkSelected(product: ProductDTO): boolean {
    return this.selectedProducts.some(p => p.code === product.code);
  }

  selectAllChanged(event: MatCheckboxChange) {
    this.isAllSelected = event.checked;
  
    if (event.checked) {
      const newAllSelected = this.storeProductsList.filter(
        p => !this.selectedProducts.some(selected => selected.code === p.code)
      );
      this.selectedProducts = [...this.selectedProducts, ...newAllSelected];
    } else {
      this.selectedProducts = this.selectedProducts.filter(
        selected => !this.storeProductsList.some(p => p.code === selected.code)
      );
    }
  }

  updateSelectAllCheckboxState(): void {
    this.isAllSelected = this.storeProductsList.every(product =>
      this.selectedProducts.some(selected => selected.code === product.code)
    );
  }

  searchStoreProducts(terms: string[]) {
    this.searchTerms = terms;
    this.loadProducts(0, 10);
  }

  getPagedData() {
    const { pageIndex, pageSize } = this.paginator;
    this.loadProducts(pageIndex * pageSize, pageSize);
    this.isAllSelected = false;
  }

  onPrintClick(): void {
    const payload = {
      settings: {
        format: 'PDF',
      },
      barcodeType: this.selectedBarcodeTypeCode,
      products: this.selectedProducts.map(p => p.code),
    };
  
    this.isLoading$.next(true);
  
    this.productService.printBarcodes(payload).subscribe({
      next: (response) => {
        const pdfBase64 = response?.data;
        if (!pdfBase64) {
          this.isLoading$.next(false);
          return;
        }
  
        const pdfSrc = response.data;
        const byteArray = new Uint8Array(
          atob(pdfSrc)
            .split("")
            .map((char) => char.charCodeAt(0))
        );
        const blob = new Blob([byteArray], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
  
        this.isLoading$.next(false);
        this.dialogRef.close(true);
      },
      error: (response) => {
        this.isLoading$.next(false);
      
        const sourceCode = response
        const fallbackMessage = this.translate.instant("SiteManagement.barCodeDialog.printBarCodeError")

        this.snackbarService.show({
          message: sourceCode
            ? this.translate.instant(sourceCode)
            : fallbackMessage,
          type: SnackbarType.ERROR,
        });
      }
    });
  }

  public onCancelClick(): void {
    this.dialogRef.close(true)
  }

  setStoreProductSearchTooltip(): string {
    const searchTooltipHint = this.translate.instant("SimpleFilter.HintTitle");
    const searchableFields = [
      this.translate.instant("SiteManagement.Code"),
      this.translate.instant("SiteManagement.Description"),
    ];
    return `${searchTooltipHint}\n${searchableFields.join("\n")}`;
  }
}